import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { RecordMark } from "@/profitability/records/record-mark";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { pct, periodLabel, points } from "./format";
import type { TrendDataset, TrendPeriod } from "./types";

const SHOWN = 12;
/**
 * The scale stops at -100% and 100%. A technician with almost no revenue can
 * sit at -1,000%, and charting that would squeeze everyone else into one
 * pixel; past the edge a dot sits on it with a caret, its figure unchanged.
 */
const FLOOR = -1;
const CEILING = 1;
/**
 * Name, the dots, this period's figure and its move, on one track for every
 * row. In a narrow card the name and figure share a line and the dots run
 * under them, beside the move, so nothing is squeezed past its card.
 */
const ROW =
  "grid min-w-0 items-center gap-x-3 grid-cols-[minmax(0,1fr)_auto] [grid-template-areas:'name_now'_'track_move'] @md:grid-cols-[minmax(0,9rem)_minmax(0,1fr)_auto_auto] @md:[grid-template-areas:'name_track_now_move']";

/**
 * Each record's margin this period against the one before, worst first: a
 * quiet dot where it was, a solid one where it is, the line between them the
 * move. A dashed line marks the target.
 */
export const SegmentDotPlot = ({
  periods,
  dataset,
  noun,
}: {
  periods: TrendPeriod[];
  dataset: TrendDataset;
  noun: string;
}) => {
  const metric = useReviewSpec().metric.label;
  const latest = periods.at(-1);
  const before = [...periods]
    .reverse()
    .slice(1)
    .find((period) => period.comparable);
  if (!latest) return null;
  const earlier = new Map(
    (before?.segments[dataset.id] ?? []).map((row) => [row.key, row.margin]),
  );
  const rows = (latest.segments[dataset.id] ?? [])
    .filter((row) => row.margin !== null)
    .sort((a, b) => (a.margin ?? 0) - (b.margin ?? 0))
    .slice(0, SHOWN);
  const target = rows.find((row) => row.target !== null)?.target ?? null;
  const values = rows.flatMap((row) => [
    row.margin ?? 0,
    earlier.get(row.key) ?? row.margin ?? 0,
  ]);
  if (target !== null) values.push(target);
  const low = Math.max(
    Math.floor((Math.min(...values) - 0.05) * 10) / 10,
    FLOOR,
  );
  const high = Math.max(
    Math.min(Math.ceil((Math.max(...values) + 0.05) * 10) / 10, CEILING),
    low + 0.1,
  );
  const clamp = (value: number) => Math.min(Math.max(value, low), high);
  const at = (value: number) =>
    `${((clamp(value) - low) / (high - low)) * 100}%`;
  const off = (value: number) =>
    value < low ? "below" : value > high ? "above" : null;
  // Only a row's current dot gets a caret; an earlier one past the scale
  // just sits at its edge.
  const current = rows.map((row) => row.margin ?? 0);
  const below = current.some((value) => off(value) === "below");
  const above = current.some((value) => off(value) === "above");
  return (
    <div className="@container flex flex-col gap-1">
      <div className={cn("pb-1 text-xs text-subtle", ROW)}>
        <span className="mx-3.5 flex justify-between tabular-nums [grid-area:track] @md:mx-0">
          <span>{pct(low, 0)}</span>
          <span>{pct(high, 0)}</span>
        </span>
        <span className="text-right whitespace-nowrap [grid-area:move]">
          {before ? `vs ${periodLabel(before.start_date, noun)}` : ""}
        </span>
      </div>
      <ul
        className="flex flex-col"
        aria-label={`${dataset.plural} by ${metric.toLowerCase()}`}
      >
        {rows.map((row) => {
          const was = earlier.get(row.key);
          const now = row.margin ?? 0;
          const change = was === undefined || was === null ? null : now - was;
          return (
            <li
              key={row.key}
              className={cn("min-h-8 gap-y-1 py-1 text-sm", ROW)}
            >
              <span className="flex min-w-0 items-center gap-2 [grid-area:name]">
                <RecordMark icon={dataset.icon} name={row.key} />
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {row.key}
                </span>
              </span>
              <span
                className="pointer-events-none relative mx-3.5 h-4 [grid-area:track] @md:mx-0"
                aria-hidden
              >
                <span className="absolute inset-x-0 top-1/2 h-px bg-line" />
                {target !== null && (
                  <span
                    className="absolute inset-y-0 w-px border-l border-dashed border-subtle"
                    style={{ left: at(target) }}
                  />
                )}
                {change !== null && was !== null && was !== undefined && (
                  <span
                    className={cn(
                      "absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full",
                      { "bg-danger": change < 0, "bg-success": change >= 0 },
                    )}
                    style={{
                      left: at(Math.min(now, was)),
                      width: `calc(${at(Math.max(now, was))} - ${at(Math.min(now, was))})`,
                    }}
                  />
                )}
                {was !== null && was !== undefined && (
                  <span
                    className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-subtle"
                    style={{ left: at(was) }}
                  />
                )}
                <span
                  className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
                  style={{ left: at(now) }}
                />
                {off(now) === "below" && (
                  <CaretLeftIcon
                    weight="bold"
                    className="absolute top-1/2 -left-3.5 size-3 -translate-y-1/2 text-ink"
                  />
                )}
                {off(now) === "above" && (
                  <CaretRightIcon
                    weight="bold"
                    className="absolute top-1/2 -right-3.5 size-3 -translate-y-1/2 text-ink"
                  />
                )}
              </span>
              <span className="text-right whitespace-nowrap tabular-nums [grid-area:now]">
                {pct(now)}
              </span>
              <Text
                render={<span />}
                variant="caption"
                tone="muted"
                tabular
                className="text-right whitespace-nowrap [grid-area:move]"
              >
                {change === null ? "" : points(change)}
              </Text>
            </li>
          );
        })}
      </ul>
      {(target !== null || below || above) && (
        <Text variant="caption" tone="muted" className="pt-1">
          {[
            target !== null && `Dashed line: target ${pct(target, 0)}.`,
            below && `A caret marks a ${metric} below ${pct(low, 0)}.`,
            above && `A caret marks a ${metric} above ${pct(high, 0)}.`,
          ]
            .filter(Boolean)
            .join(" ")}
        </Text>
      )}
    </div>
  );
};
